import { emit } from '../events';
import { configureAudioSession } from './audioSession';
import {
  canRecreateAudioContext,
  isUnlocked,
  recreateAudioContext,
  unlockAudio,
} from './audioUnlock';
import { clearKeyEchoBuffers } from './keyEcho';
import { checkAlive, usePiper } from './piper/usePiper';
import { warmUpTts } from './warmup';

/**
 * Put the audio path back together, from inside the tap that asked (`SPK-19`).
 *
 * ── The order is the requirement ──
 *
 * Everything that needs the gesture happens **before the first `await`**, and everything else happens
 * after. On Safari the silent-sample unlock only works inside a user gesture, and an `await` spends it —
 * so a repair that did its tidying first would arrive at the one operation that needs a gesture having
 * just given it away. That is C71's failure mode reappearing inside the control built to fix it, which is
 * why this reads as a sequence with a comment on each step rather than as a tidy list of awaits.
 *
 * ── What is deliberately last ──
 *
 * Restarting the Piper worker costs its ninety-megabyte session and about a second. That is an acceptable
 * price for a repair a human asked for and an unacceptable one anywhere automatic, so it lives here and
 * only here, and only when the worker fails to answer a ping.
 *
 * Components never import this: `audioStatusStore.repairFromGesture()` wraps it, so a surface stays
 * ignorant of the mechanism (`CON-10`).
 */

export type RepairOutcome =
  | { ok: true; replaced: boolean }
  | { ok: false; reason: 'exhausted' | 'no_context' | 'still_not_running' };

export async function repairAudio(): Promise<RepairOutcome> {
  const running = isUnlocked();

  /**
   * Step 1, synchronous: replace the context if it is not already working.
   *
   * `force` because a human asked. The interval bound exists to stop `ensureRunning()` building a context
   * per utterance on the automatic path; a person tapping twice is the case where the second tap must do
   * something. The *count* bound is Safari's and is never skipped — when it refuses there is genuinely
   * nothing left in-page to try, which is what `SHL-09`'s reload is offered against.
   */
  let replaced = false;
  if (!running) {
    if (!canRecreateAudioContext(true)) {
      const outcome: RepairOutcome = { ok: false, reason: 'exhausted' };
      emit({ type: 'AudioRepairAttempted', ok: false });
      return outcome;
    }
    if (!recreateAudioContext({ force: true })) {
      emit({ type: 'AudioRepairAttempted', ok: false });
      return { ok: false, reason: 'no_context' };
    }
    replaced = true;
  }

  // Step 2, still inside the gesture: `unlockAudio()` starts the silent sample before its own first
  // await, which is the whole reason it is safe to call here (see `audioUnlock.ts`).
  const unlocked = await unlockAudio();

  /**
   * Step 3, after `running` is decided. None of this needs the gesture, and none of it may be allowed to
   * delay the answer the person is waiting for — so each is fire-and-forget or cheap.
   */
  if (replaced) {
    // These buffers were decoded by the context that just died and carry its sample rate.
    clearKeyEchoBuffers();
  }
  // Navigator-level, so it *should* survive a replacement. Nothing measured says it does, and it is free.
  configureAudioSession();
  // A cold host is the other live explanation for the report this repair exists to answer (C80).
  void warmUpTts(undefined, { force: true });

  await reviveOfflineVoiceIfDead();

  if (!unlocked) {
    emit({ type: 'AudioRepairAttempted', ok: false });
    return { ok: false, reason: 'still_not_running' };
  }

  emit({ type: 'AudioRepairAttempted', ok: true });
  return { ok: true, replaced };
}

/**
 * Ask the offline voice whether it is still there, and rebuild it only if it is not (`SPK-20`).
 *
 * `ready` is a flag set when the worker started, so a worker iOS killed while the app was backgrounded
 * still reads as ready. A ping is the only way to tell that from a worker that is merely busy — and
 * telling them apart is what stops this throwing away a working session.
 */
async function reviveOfflineVoiceIfDead(): Promise<void> {
  const piper = usePiper.getState();
  if (piper.available !== true) return;

  if (piper.ready && (await checkAlive())) return;

  console.info('[audio] offline voice did not answer; rebuilding its session');
  piper.stopWorker();
  try {
    await piper.start();
  } catch (err) {
    // A voice that will not come back is not a reason for the repair to fail: the chain has four other
    // providers and the person is waiting on the one that speaks now.
    console.warn('[audio] offline voice could not be restarted', err);
  }
}
